import { useMutation, useQuery } from "@tanstack/react-query";
import useAxiosInstance from "./axiosInstance.utils";
import { errorFormat } from "./handleError.utils";
import { onError, onSuccess } from "./toastify.utils";
import { useSelector } from "react-redux";
import { onSuccessMessage, queryClient } from "./reactQuery.utils";
import { useEffect, useState } from "react";
import { arrayMap } from "./array.utils";

/**
 * :::::::MUTATIONS:::::::::::
 */

export function useCreateUserMutation(keyData) {
  const { postApi } = useAxiosInstance();
  async function createUserApi(data) {
    try {
      const api = `/account/register`;
      const apiResponse = await postApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }
  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["createUserApi", ...keyData],
    mutationFn: (data) => createUserApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("users");
      onSuccessMessage(data, 201);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, createUser: mutateAsync };
}

export function useUpdateUserMutation(keyData) {
  const { putApi } = useAxiosInstance();
  async function updateUserApi(data) {
    try {
      const api = `/user`;
      const apiResponse = await putApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["updateUserApi", ...keyData],
    mutationFn: (data) => updateUserApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("users");
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, updateUser: mutateAsync };
}

export function useDeleteUserMutation(keyData) {
  const { deleteApi } = useAxiosInstance();
  async function deleteUserApi(id) {
    try {
      const api = `/user/${id}`;
      const apiResponse = await deleteApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["deleteUserApi", ...keyData],
    mutationFn: (data) => deleteUserApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries("users");
      onSuccessMessage(data, 200);
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, createUser: mutateAsync };
}

export function useUserLoginMutation(keyData) {
  const { postApi } = useAxiosInstance();
  async function userLoginApi(data) {
    try {
      const api = `/account/login`;
      const apiResponse = await postApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["userLoginApi", ...keyData],
    mutationFn: (data) => userLoginApi(data),
    onSuccess: (data) => {
      if (data?.status !== 200) {
        onError(data?.message);
      }

      if (data?.status === 200) {
        onSuccess("Successfully login!");
      }
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, userLogin: mutateAsync };
}

export function useUserLogoutMutation(keyData) {
  const { postApi } = useAxiosInstance();
  async function userLogoutApi(data) {
    try {
      const api = `/account/logout`;
      const apiResponse = await postApi(api, data);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { isPending: loading, mutateAsync } = useMutation({
    mutationKey: ["userLogoutApi", ...keyData],
    mutationFn: (data) => userLogoutApi(data),
    onSuccess: (data) => {
      if (data?.status !== 200) {
        onError(data?.message);
      } else {
        onSuccess("Successfully logout!");
      }
    },
    onError: (err) => {
      return errorFormat(err);
    }
  });

  return { loading, userLogout: mutateAsync };
}

/**
 * :::::::QUERIES:::::::::::
 */
export function useGetUserQuery({ username, options = {} }) {
  const { getApi } = useAxiosInstance();
  async function getUserApi(id) {
    try {
      const api = `/user/${id}`;
      const apiResponse = await getApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }

  const { data, isPending: loading } = useQuery({
    queryKey: [`getUser-${username ?? ""}`],
    queryFn: () => getUserApi(username),
    ...options
  });
  const newData = { ...data?.data?.user };
  return { data: newData, loading };
}

export function useGetAllUserQuery({ query, options = {} }) {
  const { getApi } = useAxiosInstance();
  async function getAllUserApi(query) {
    try {
      const api = `/users${query ?? ""}`;
      const apiResponse = await getApi(api);
      return apiResponse;
    } catch (error) {
      return errorFormat(error);
    }
  }
  const { data, isPending: loading } = useQuery({
    queryKey: [`getAllUser-${query ?? ""}`],
    queryFn: () => getAllUserApi(query),
    ...options
  });
  const newData = data?.data?.users ?? {};
  return { data: newData, loading };
}

// get all users for dropdowns
export function useGetAllUserAsDropdown(query) {
  const { data, loading } = useGetAllUserQuery({ query });
  const [dropdowns, setDropdowns] = useState([]);

  useEffect(() => {
    if (data?.length) {
      const newData =
        arrayMap(data, (item) => ({ label: item?.name ?? "-", value: item?.id ?? "" })) || [];
      setDropdowns(newData);
    }
  }, [data]);

  return { data: dropdowns, loading };
}

/**
 * :::::::HOOKS:::::::::::
 */

export function useAuthFromRedux() {
  const auth = useSelector((state) => state.auth);
  return auth;
}
